/** Anita 4-band scale: same cutoffs for Face, Voice, Wearable OWS, and Lime Momentum. */

export type ScoreBandId = 'low' | 'red' | 'amber' | 'green';

export interface ScoreBandDefinition {
  readonly id: ScoreBandId;
  readonly label: string;
  readonly minInclusive: number;
  readonly maxInclusive: number;
  readonly color: string;
  readonly labelColor: string;
}

/** Single source of truth for band cutoffs, colours, and labels. */
export const SCORE_BAND_DEFINITIONS: ReadonlyArray<ScoreBandDefinition> = [
  { id: 'low', label: 'Low', minInclusive: 0, maxInclusive: 20, color: '#b7dee8', labelColor: '#004b4d' },
  { id: 'red', label: 'Red', minInclusive: 21, maxInclusive: 40, color: '#e24f4f', labelColor: '#ffffff' },
  { id: 'amber', label: 'Amber', minInclusive: 41, maxInclusive: 60, color: '#f2c94c', labelColor: '#004b4d' },
  { id: 'green', label: 'Green', minInclusive: 61, maxInclusive: 100, color: '#8dc044', labelColor: '#004b4d' },
];

export const SCORE_BAND_COLORS = Object.fromEntries(
  SCORE_BAND_DEFINITIONS.map((band) => [band.id, band.color]),
) as Record<ScoreBandId, string>;

const SCORE_SCALE_MAX = 100;

function clampScore(score: number): number {
  return Math.min(SCORE_SCALE_MAX, Math.max(0, Number(score)));
}

function findScoreBand(score: number): ScoreBandDefinition {
  const clamped = clampScore(score);
  return (
    SCORE_BAND_DEFINITIONS.find(
      (band) => clamped >= band.minInclusive && clamped <= band.maxInclusive,
    ) ?? SCORE_BAND_DEFINITIONS[SCORE_BAND_DEFINITIONS.length - 1]
  );
}

function bandScoreCount(band: ScoreBandDefinition): number {
  return band.maxInclusive - band.minInclusive + 1;
}

function formatBandRange(band: ScoreBandDefinition): string {
  return `${band.minInclusive}–${band.maxInclusive}`;
}

export function scoreBandColor(score: number): string {
  return findScoreBand(score).color;
}

export function scoreBandLabelColor(background: string): string {
  return SCORE_BAND_DEFINITIONS.find((band) => band.color === background)?.labelColor ?? '#004b4d';
}

export function scoreBandGradientStops(): ReadonlyArray<{ offset: number; color: string }> {
  const stops: Array<{ offset: number; color: string }> = [];

  SCORE_BAND_DEFINITIONS.forEach((band) => {
    stops.push({ offset: band.minInclusive, color: band.color });
    stops.push({ offset: Math.min(SCORE_SCALE_MAX, band.maxInclusive), color: band.color });
  });

  return stops;
}

/** CSS/SVG gradient with high scores at offset 0 (top). */
export function scoreBandGradientStopsHighAtTop(): ReadonlyArray<{ offset: number; color: string }> {
  return scoreBandGradientStops().map((stop) => ({
    offset: Math.max(0, Math.min(SCORE_SCALE_MAX, SCORE_SCALE_MAX - stop.offset)),
    color: stop.color,
  }));
}

/** Gauge bar widths on the 0–100 score axis, proportional to integer scores per band. */
export function scoreBandScaleSegments(): ReadonlyArray<{ weight: number; color: string }> {
  const scoreCount = SCORE_SCALE_MAX + 1;

  return SCORE_BAND_DEFINITIONS.map((band) => ({
    weight: bandScoreCount(band) / scoreCount,
    color: band.color,
  }));
}

export interface ScoreBandLegendItem {
  label: string;
  range: string;
  color: string;
}

export function scoreBandLegendItems(): ReadonlyArray<ScoreBandLegendItem> {
  return SCORE_BAND_DEFINITIONS.map((band) => ({
    label: band.label,
    range: formatBandRange(band),
    color: band.color,
  }));
}
